import type { CampaignRegime } from '../../campaign/campaign.types';
import { countryName } from '../../shared/feed/countries';
import { monogram, REGIME_LABELS } from '../../shared/feed/format';
import { OTHER_SECTOR_SLUG, sectorBySlug } from '../../shared/sectors/taxonomy';
import { countLabel, type FactPlatform, type SiteFacts } from './facts';
import { PROMO_ICON_PATHS } from './promo';

/**
 * The browse pages: the public pages that list raises by something (the
 * live index, the week's closes and launches, a country, a regulation, a
 * sector, a platform), the platform directory, and the guides and
 * statistics. The home page's "Browse raises" and every page's footer link
 * them (BrowseService, BrowseLocals).
 *
 * The list names pages that are not built yet, at the address each is meant
 * to have. A link is drawn only when the app's router sends a GET for its
 * path to the page's own route (PublicRoutes): so a hub page shows up in
 * both places the day it ships at its address here, with nothing else to
 * change, and until then nothing links it. A page that ships at another
 * address changes its `route` here, the one place it is named.
 *
 * Pure, as the rest of site/: the server's view models use it, and the
 * specs test it without a router.
 */

export type BrowseId =
  | 'live'
  | 'closing-this-week'
  | 'new-this-week'
  | 'near-cap'
  | 'country'
  | 'regulation'
  | 'sector'
  | 'platform-raises'
  | 'platform-profiles'
  | 'platforms'
  | 'guides'
  | 'statistics';

export type BrowseGroupId =
  'now' | 'country' | 'regulation' | 'sector' | 'platform' | 'learn';

/** The statistics page (StatisticsController), under /data as it is data, not a guide. */
export const STATISTICS_PATH = '/data/equity-crowdfunding-statistics';

/** One link to a browse page. */
export interface BrowseLink {
  /** From the site's root, as the page names itself canonical: "/raises/platform/crowdcube". */
  href: string;
  /** Where it goes, read on its own: "UK equity crowdfunding raises", never "more". */
  text: string;
}

/** A link with what its tile on the home page shows beside its words. */
export interface BrowseEntry extends BrowseLink {
  /** Its short name: "United Kingdom", "Wefunder", "Fintech". */
  label: string;
  /** Its badge: a regulation ("Reg CF"), a platform's monogram ("W"). */
  code?: string;
  /** A country's flag, from the site's root: flagPath. */
  flag?: string;
  /** A week's list's icon: the paths of a 24px line icon. */
  icon?: readonly string[];
  /** The line under a platform tile's name: "Live raises". */
  sub?: string;
}

/**
 * What the home page's tiles show: the short label, the words kept as each
 * link's accessible name; or the words themselves, should the short anchor
 * text cost the hub pages in search.
 */
export type BrowseLabelMode = 'short' | 'full';

export const BROWSE_LABEL_MODE: BrowseLabelMode = 'short';

/** One of the site's sectors, with how many live raises are filed under it (sectorCounts). */
export interface BrowseSector {
  /** "food-and-drink" */
  slug: string;
  /** "Food and drink" */
  name: string;
  /** Live raises in scope filed under it. */
  count: number;
}

/** What the families' links are made of. Either may be null: unread, or not needed. */
export interface BrowseData {
  facts: Pick<
    SiteFacts,
    'busiest' | 'platforms' | 'edgar' | 'platformCount'
  > | null;
  /** Live sectors, most raises first; read only while a sector page is served. */
  sectors: readonly BrowseSector[] | null;
}

export interface BrowsePage {
  id: BrowseId;
  group: BrowseGroupId;
  /**
   * The route that must answer the page's links, exactly as a controller
   * declares it, parameter's name and all: "/raises/:country", which is not
   * RaiseController's "/raises/:slug" though it matches the same addresses.
   */
  route: string;
  /** Its links: one for a page, one per country (sector, platform…) for a family. */
  links(data: BrowseData): BrowseEntry[];
}

/**
 * Whether the router sends a GET for `path` ("/raises/germany") to `route`
 * ("/raises/:country"): PublicRoutes.resolves on the server.
 */
export type Served = (path: string, route: string) => boolean;

/** How many of a family's links the home page lists, at most. */
export const FAMILY_LIMIT = 12;

/** How many links the footer's Browse column holds. */
export const FOOTER_LINKS = 8;

const page = (
  id: BrowseId,
  group: BrowseGroupId,
  route: string,
  text: string,
  label: string,
  more: {
    icon?: readonly string[];
    sub?: (data: BrowseData) => string | null;
  } = {},
): BrowsePage => ({
  id,
  group,
  route,
  links: (data) => {
    const line = more.sub?.(data) ?? null;
    return [
      {
        href: route,
        text,
        label,
        ...(more.icon ? { icon: more.icon } : {}),
        ...(line ? { sub: line } : {}),
      },
    ];
  },
});

/** The icons of the week's lists' tiles, in the /raises app panel's line style. */
const WEEK_LIST_ICONS = {
  /** As the panel's close reminder has it. */
  closing: PROMO_ICON_PATHS.clock,
  /** A spark: what has just appeared. */
  opened: [
    'M9.9 15.5a2 2 0 0 0-1.4-1.4l-6.1-1.6a.5.5 0 0 1 0-1l6.1-1.6a2 2 0 0 0 1.4-1.4l1.6-6.1a.5.5 0 0 1 1 0l1.6 6.1a2 2 0 0 0 1.4 1.4l6.1 1.6a.5.5 0 0 1 0 1l-6.1 1.6a2 2 0 0 0-1.4 1.4l-1.6 6.1a.5.5 0 0 1-1 0z',
  ],
  /** A gauge, its needle near the top of the scale. */
  nearCap: ['M3.34 19a10 10 0 1 1 17.32 0', 'm12 14 4-4'],
} as const;

/** Where /static serves the flags from (site-flags.ts). */
export const FLAGS_URL = '/static/build/flags/';

/** A country's flag, by its ISO code: "/static/build/flags/GB.svg". */
export const flagPath = (code: string) =>
  `${FLAGS_URL}${code.toUpperCase()}.svg`;

/** The platforms read today, as the facts list them; none where unread. */
function readPlatforms(data: BrowseData): FactPlatform[] {
  return (data.facts?.platforms ?? []).filter((p) => !p.planned);
}

/**
 * The platforms with live raises, busiest first, as the facts name them
 * (`busiest`, the About block's sonar): the ones whose list is not empty.
 */
function busiestPlatforms(data: BrowseData): FactPlatform[] {
  const byName = new Map(readPlatforms(data).map((p) => [p.name, p]));
  return (data.facts?.busiest ?? []).flatMap((name) => {
    const platform = byName.get(name);
    return platform ? [platform] : [];
  });
}

/** Every platform read today, the busiest first, then by name. */
function platformsByBusy(data: BrowseData): FactPlatform[] {
  const busiest = busiestPlatforms(data);
  const rest = readPlatforms(data)
    .filter((p) => !busiest.includes(p))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...busiest, ...rest];
}

/** How a country's raises are named, where "{name} equity crowdfunding raises" reads well. */
export const COUNTRY_WORDS: Readonly<Record<string, string>> = {
  AT: 'Austrian',
  AU: 'Australian',
  BE: 'Belgian',
  BR: 'Brazilian',
  CA: 'Canadian',
  DE: 'German',
  ES: 'Spanish',
  FI: 'Finnish',
  FR: 'French',
  GB: 'UK',
  IT: 'Italian',
  MY: 'Malaysian',
  NL: 'Dutch',
  NZ: 'New Zealand',
  PL: 'Polish',
  RO: 'Romanian',
  SK: 'Slovak',
  US: 'US',
};

/** A country's name in lower-case ASCII, words joined by hyphens: "Côte d’Ivoire" → "cote-d-ivoire". */
export function asciiSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The two countries everyone writes short, and the names Intl adds a note to.
 * Every other country's hub is its name (`countrySlug`).
 */
export const COUNTRY_SLUGS: Readonly<Record<string, string>> = {
  GB: 'uk',
  US: 'us',
  HK: 'hong-kong',
  MO: 'macao',
  MM: 'myanmar',
};

/** A country's hub, the end of its address: "uk", "germany", "new-zealand". */
export function countrySlug(code: string): string {
  const upper = code.toUpperCase();
  return Object.hasOwn(COUNTRY_SLUGS, upper)
    ? COUNTRY_SLUGS[upper]
    : asciiSlug(countryName(upper));
}

/** "UK equity crowdfunding raises"; "Equity crowdfunding raises in Estonia" for a country without a word above. */
export function countryText(code: string): string {
  const word = COUNTRY_WORDS[code];
  return word
    ? `${word} equity crowdfunding raises`
    : `Equity crowdfunding raises in ${countryName(code)}`;
}

/**
 * The countries the platforms read today are in, the facts' countries,
 * with the US for SEC EDGAR: the most platforms first, then by name.
 */
function countries(data: BrowseData): string[] {
  const platforms = new Map<string, number>();
  for (const p of readPlatforms(data)) {
    if (p.country)
      platforms.set(p.country, (platforms.get(p.country) ?? 0) + 1);
  }
  if (data.facts?.edgar && !platforms.has('US')) platforms.set('US', 0);
  return [...platforms]
    .sort(
      ([a, m], [b, n]) => n - m || countryName(a).localeCompare(countryName(b)),
    )
    .map(([code]) => code);
}

/**
 * A profile's address is its platform's id, but for a portal whose id is a name
 * it no longer trades under: that id stays on every row (and on its raise hub),
 * and the id's address answers 301 here.
 */
const PROFILE_SLUGS: Readonly<Record<string, string>> = {
  picmii: 'highlander',
};

export function profileSlug(id: string): string {
  return Object.hasOwn(PROFILE_SLUGS, id) ? PROFILE_SLUGS[id] : id;
}

/** A regulation a raise can be under that has a page: every one but OTHER. */
export type Regulation = Exclude<CampaignRegime, 'OTHER'>;

/** A raise's regulation, as its link says it; OTHER has no page. */
export const REGULATION_TEXT: Readonly<Record<Regulation, string>> = {
  REG_CF: 'Reg CF raises',
  FCA: 'FCA-regulated UK raises',
  ECSPR: 'ECSPR raises in the EU',
  REG_A: 'Reg A+ raises',
  CSF: 'Australian CSF raises',
  NI_45_110: 'Canadian NI 45-110 raises',
};

/** Where a regulation applies: its tile's name, beside the regulation's badge. */
export const REGULATION_PLACE: Readonly<Record<Regulation, string>> = {
  REG_CF: 'United States',
  FCA: 'United Kingdom',
  ECSPR: 'European Union',
  REG_A: 'United States',
  CSF: 'Australia',
  NI_45_110: 'Canada',
};

/** Every regulation with a page, in REGULATION_TEXT's order. */
export const REGULATIONS = Object.keys(REGULATION_TEXT) as Regulation[];

/**
 * A regulation's hub, the end of its address. One segment under /raises, beside
 * the countries' and the raises' own, so a word that says where it applies when
 * the regime's name alone does not ("csf-australia").
 */
const REGULATION_SLUGS: Readonly<Record<Regulation, string>> = {
  REG_CF: 'reg-cf',
  FCA: 'fca-uk',
  ECSPR: 'ecspr',
  REG_A: 'reg-a',
  CSF: 'csf-australia',
  NI_45_110: 'ni-45-110-canada',
};

/**
 * The regulations a raise can be under (CampaignRegime) that a platform read
 * today offers under, and Reg CF for SEC EDGAR, in REGULATION_TEXT's order.
 * A platform's other regimes (§34f, CVM…) are no raise's, so have no page.
 */
function regulations(data: BrowseData): Regulation[] {
  const offered = new Set(readPlatforms(data).flatMap((p) => p.regimes));
  if (data.facts?.edgar) offered.add('REG_CF');
  return REGULATIONS.filter((regime) => offered.has(regime));
}

/** "REG_CF" → "reg-cf", "FCA" → "fca-uk". */
export const regulationSlug = (regime: Regulation) => REGULATION_SLUGS[regime];

/** What names one member of a hub family, as a raise or the facts hold it. */
export interface HubMembers {
  /** ISO code: "GB". */
  country: string;
  regulation: CampaignRegime;
  /** One of the site's sectors, by its slug: "food-and-drink". */
  sector: string;
  /** The platform's profile. */
  platform: { id: string; name: string };
  /** Every live raise on the platform. */
  platformRaises: { id: string; name: string };
}

export type HubId = keyof HubMembers;

interface Hub<T> {
  /** As a controller would declare it. */
  route: string;
  /** Its page's slug, its link's words and its tile's; null for a member with no page. */
  member(value: T): (Omit<BrowseEntry, 'href'> & { slug: string }) | null;
}

/**
 * The hub families, the one place their addresses and words are made: a
 * page per country, regulation, sector and platform (its profile, and its
 * live raises), at `route` with the member's slug as its parameter. The
 * OTHER regime has no page. Nor has a word that is not one of the site's sectors;
 * and "other" answers, for the addresses that moved there, but is never linked.
 */
const HUBS: { [K in HubId]: Hub<HubMembers[K]> } = {
  country: {
    // One segment, declared ahead of raises/:slug (CountryHubController). The
    // old address, /raises/country/gb, answers 301 here.
    route: '/raises/:country',
    member: (code) =>
      code
        ? {
            slug: countrySlug(code),
            text: countryText(code),
            label: countryName(code),
            flag: flagPath(code),
          }
        : null,
  },
  regulation: {
    // One segment, declared ahead of raises/:country and raises/:slug
    // (RegulationHubController). The old address, /raises/regulation/fca,
    // answers 301 here.
    route: '/raises/:regulation',
    member: (regime) =>
      regime === 'OTHER'
        ? null
        : {
            slug: regulationSlug(regime),
            text: REGULATION_TEXT[regime],
            label: REGULATION_PLACE[regime],
            code: REGIME_LABELS[regime],
          },
  },
  sector: {
    route: '/raises/sector/:sector',
    member: (slug) => {
      const sector = sectorBySlug(slug);
      return !sector || sector.slug === OTHER_SECTOR_SLUG
        ? null
        : {
            slug: sector.slug,
            text: `${sector.name} equity crowdfunding raises`,
            label: sector.name,
          };
    },
  },
  platform: {
    route: '/platforms/:platform',
    member: (p) => ({
      slug: profileSlug(p.id),
      text: `${p.name} platform profile`,
      label: p.name,
      code: monogram(p.name),
      sub: 'Platform profile',
    }),
  },
  platformRaises: {
    route: '/raises/platform/:platform',
    member: (p) => ({
      slug: p.id,
      text: `Live raises on ${p.name}`,
      label: p.name,
      code: monogram(p.name),
      sub: 'Live raises',
    }),
  },
};

/** The sector pages' route: BrowseService reads the live sectors only while it is served. */
export const SECTOR_ROUTE = HUBS.sector.route;

/** Each family's route, as its controller declares it: "/raises/:country". */
export const HUB_ROUTES = {
  country: HUBS.country.route,
  regulation: HUBS.regulation.route,
  sector: HUBS.sector.route,
  platform: HUBS.platform.route,
  platformRaises: HUBS.platformRaises.route,
} as const satisfies Record<HubId, string>;

/** A member's link, whether or not its page exists. */
function memberLink<K extends HubId>(
  id: K,
  value: HubMembers[K],
): BrowseEntry | null {
  const hub: Hub<HubMembers[K]> = HUBS[id];
  const member = hub.member(value);
  if (!member) return null;
  const { slug, ...entry } = member;
  return {
    href: hub.route.replace(/:[^/]+/, encodeURIComponent(slug)),
    ...entry,
  };
}

/**
 * A member's page's address, "/raises/uk", whether or not the page
 * exists; null for a member with no page (OTHER, a raise with no sector).
 */
export function hubPath<K extends HubId>(
  id: K,
  value: HubMembers[K],
): string | null {
  return memberLink(id, value)?.href ?? null;
}

/**
 * The link to one member's hub page ("UK equity crowdfunding raises",
 * /raises/uk), or null while the router does not send its path to
 * the hub's route: the page is not built, or another route answers first.
 * Every hub link is made here, the browse links' and a raise page's alike,
 * so each shows up the day its page ships.
 */
export function hubLink<K extends HubId>(
  id: K,
  value: HubMembers[K],
  served: Served,
): BrowseLink | null {
  const link = memberLink(id, value);
  return link && served(pathOf(link.href), HUBS[id].route)
    ? { href: link.href, text: link.text }
    : null;
}

/** A family: a link per member that has a page, at its hub's route. */
const family = <K extends HubId>(
  id: BrowseId,
  group: BrowseGroupId,
  hub: K,
  members: (data: BrowseData) => HubMembers[K][],
): BrowsePage => ({
  id,
  group,
  route: HUBS[hub].route,
  links: (data) =>
    members(data).flatMap((value) => {
      const link = memberLink(hub, value);
      return link ? [link] : [];
    }),
});

/**
 * Every browse page, in the order the home page lists them. Single pages
 * are one link; a family (country, regulation, sector, platform) is a link
 * per member, for the members its page has raises for, as far as the
 * site's facts can tell.
 */
export const BROWSE_PAGES: readonly BrowsePage[] = [
  page(
    'live',
    'now',
    '/raises',
    'Live equity crowdfunding raises',
    'Live raises',
  ),
  page(
    'closing-this-week',
    'now',
    '/raises/closing-this-week',
    'Equity crowdfunding raises closing this week',
    'Closing this week',
    { icon: WEEK_LIST_ICONS.closing },
  ),
  page(
    'new-this-week',
    'now',
    '/raises/new-this-week',
    'New equity crowdfunding raises this week',
    'New this week',
    { icon: WEEK_LIST_ICONS.opened },
  ),
  page(
    'near-cap',
    'now',
    '/raises/near-cap',
    'Equity crowdfunding raises near their cap',
    'Near their cap',
    { icon: WEEK_LIST_ICONS.nearCap },
  ),
  family('country', 'country', 'country', countries),
  family('regulation', 'regulation', 'regulation', regulations),
  family('sector', 'sector', 'sector', (data) =>
    (data.sectors ?? []).filter((s) => s.count > 0).map((s) => s.slug),
  ),
  // Every live raise on one platform, which a raise page's "All Crowdcube
  // raises" links too: the busiest platforms, whose lists are not empty.
  family('platform-raises', 'platform', 'platformRaises', busiestPlatforms),
  family('platform-profiles', 'platform', 'platform', platformsByBusy),
  page(
    'platforms',
    'platform',
    '/platforms',
    'Equity crowdfunding platform directory',
    'Platform directory',
    {
      // The site's platform count, as every page states it; none unread.
      sub: (data) => {
        const count = data.facts?.platformCount ?? null;
        return count !== null && count > 1
          ? `All ${countLabel(count)} platforms`
          : null;
      },
    },
  ),
  page('guides', 'learn', '/guides', 'Equity crowdfunding guides', 'Guides'),
  page(
    'statistics',
    'learn',
    STATISTICS_PATH,
    'Equity crowdfunding statistics',
    'Statistics',
  ),
];

/**
 * The home page's groups, in its order (views/home.hbs #browse): each
 * card's heading, and what it sorts by, printed beside it.
 */
export const BROWSE_GROUPS: readonly {
  id: BrowseGroupId;
  heading: string;
  note: string | null;
}[] = [
  { id: 'now', heading: 'Right now', note: null },
  { id: 'country', heading: 'By country', note: 'Where the company is based' },
  { id: 'sector', heading: 'By sector', note: 'What the company does' },
  {
    id: 'regulation',
    heading: 'By regulation',
    note: 'The rules it raises under',
  },
  { id: 'platform', heading: 'By platform', note: 'Where it lists' },
  { id: 'learn', heading: 'Guides and statistics', note: null },
];

/**
 * Each group's page set apart as its card's feature: "Right now"'s is the
 * live index, the whole of a dark card; "By platform"'s the directory, the
 * last tile, drawn dashed.
 */
const FEATURES: Partial<Record<BrowseGroupId, BrowseId>> = {
  now: 'live',
  platform: 'platforms',
};

/**
 * The footer's Browse column: the first FOOTER_LINKS links, taking at most
 * this many from each page, in this order. The week's lists and the top two
 * countries first, the busiest platforms filling in while the hubs are not
 * built.
 */
export const FOOTER_ORDER: readonly [BrowseId, number][] = [
  ['live', 1],
  ['closing-this-week', 1],
  ['new-this-week', 1],
  ['near-cap', 1],
  ['country', 2],
  ['statistics', 1],
  ['guides', 1],
  ['platforms', 1],
  ['platform-raises', 6],
  ['regulation', 1],
  ['sector', 1],
  ['platform-profiles', 1],
];

/** A link's group as browse_link_clicked names it: its card's, the directory its own. */
export type BrowseTrack = BrowseGroupId | 'directory';

/** A link as the home page's tiles draw it. */
export interface BrowseTile extends BrowseLink {
  /** What the tile shows: its short label, or its words in full mode. */
  label: string;
  code: string | null;
  flag: string | null;
  icon: readonly string[] | null;
  /** Short mode only: in full mode the words already say it. */
  sub: string | null;
  track: BrowseTrack;
}

/** One card of the home page's "Browse raises". */
export interface BrowseCard {
  id: BrowseGroupId;
  heading: string;
  note: string | null;
  /** The group's page set apart (FEATURES), while it is served. */
  feature: BrowseTile | null;
  /** The rest, at most FAMILY_LIMIT of each family. */
  links: BrowseTile[];
}

/** `{{browse.…}}` on every page (BrowseLocals). */
export interface BrowseView {
  /** The home page's "Browse raises": each card with a link, in order. */
  groups: BrowseCard[];
  /** The same cards by group, for the template to place. */
  cards: Partial<Record<BrowseGroupId, BrowseCard>>;
  /** The footer's Browse column, at most FOOTER_LINKS. */
  footer: BrowseLink[];
}

/** "/raises?sort=funded#x" → "/raises": what the router is asked. */
export function pathOf(href: string): string {
  return href.replace(/[?#].*$/, '');
}

/** An entry as its tile shows it, in `mode`. */
function tile(
  entry: BrowseEntry,
  track: BrowseTrack,
  mode: BrowseLabelMode,
): BrowseTile {
  const full = mode === 'full';
  return {
    href: entry.href,
    text: entry.text,
    label: full ? entry.text : entry.label,
    code: entry.code ?? null,
    flag: entry.flag ?? null,
    icon: entry.icon ?? null,
    sub: full ? null : (entry.sub ?? null),
    track,
  };
}

/**
 * The links to draw: each page's, kept where `served` says the router
 * sends its path to the page's route, grouped into the home page's cards
 * and cut down for the footer.
 */
export function browseView(
  data: BrowseData,
  served: Served,
  mode: BrowseLabelMode = BROWSE_LABEL_MODE,
): BrowseView {
  const links = new Map<BrowseId, BrowseEntry[]>();
  for (const p of BROWSE_PAGES) {
    links.set(
      p.id,
      p.links(data).filter((link) => served(pathOf(link.href), p.route)),
    );
  }
  const tiles = (p: BrowsePage) =>
    (links.get(p.id) ?? [])
      .slice(0, FAMILY_LIMIT)
      .map((entry) =>
        tile(entry, p.id === 'platforms' ? 'directory' : p.group, mode),
      );
  const groups = BROWSE_GROUPS.map(({ id, heading, note }): BrowseCard => {
    const pages = BROWSE_PAGES.filter((p) => p.group === id);
    const featured = pages.find((p) => p.id === FEATURES[id]);
    return {
      id,
      heading,
      note,
      feature: (featured && tiles(featured)[0]) ?? null,
      links: pages.filter((p) => p !== featured).flatMap(tiles),
    };
  }).filter((card) => card.feature !== null || card.links.length > 0);
  const footer = FOOTER_ORDER.flatMap(([id, take]) =>
    (links.get(id) ?? [])
      .slice(0, take)
      .map(({ href, text }) => ({ href, text })),
  ).slice(0, FOOTER_LINKS);
  return {
    groups,
    cards: Object.fromEntries(groups.map((card) => [card.id, card])),
    footer,
  };
}
